// src/models/AssetModel.js

export class AssetModel {
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "assetId",
    "assetName",
    "assetDescription",
    "companyId",

    // Classification
    "assetType",
    "ownershipType",

    // Parent-child
    "parentAssetName",
    "parentAssetId",
    "purchasedSeparately",

    // Categorization
    "categoryId",
    "subCategoryId",
    "brandId",

    // Tracking
    "serializationType",
    "manufacturerSerialNumber",
    "userTrackingMode",
    "quantity",
    "unitOfMeasurement",
    "reorderLevel",
    "expiryDate",
    "status",

    "isTagged",
    "taggedAt",
    "taggedBy",
    "tagPhotoUrl",

    // Assignment
    "assignedTo",
    "assignedToName",
    "assignedDepartmentId",
    "assignedDepartmentName",
    "assignedOn",
    "assignmentHistory",

    // Asset Ownership
    "ownerId",
    "ownerEmail",
    "ownerName",
    "ownerAssignedOn",
    "ownershipHistory",

    // Location
    "locationCode",
    "locationHistory",

    // Sales & Warranty (Customer Asset)
    "salesDate",
    "salesValue",
    "customerId",
    "customerName",
    "salesInvoiceNumber",
    "salesInvoiceFileUrl",
    "salesInvoiceFileName",

    // Owned
    "purchaseDate",
    "purchaseValue",
    "vendorId",
    "warrantyExpiry",
    "warrantyHistory",
    "invoiceNumber",
    "invoiceFileUrl",

    // Leased
    "leaseStartDate",
    "leaseEndDate",
    "leaseAmount",
    "leaseVendorId",
    "leaseVendorName",
    "leaseAgreementNumber",
    "leaseBillingFrequency",

    // Rented
    "rentStartDate",
    "expectedReturnDate",
    "rentAmount",
    "rentVendorId",
    "rentVendorName",
    "rentAgreementNumber",
    "rentBillingFrequency",

    // Depreciation
    "depreciationMethod",
    "usefulLife",
    "depreciationRate",
    "residualValue",
    "openingBookValue",
    "isOpeningBookValueManual",
    "isDepreciationAuto",
    "depreciationSchedule",

    // Maintenance
    "maintenanceFrequency",
    "maintenanceStartDate",

    // QR
    "qrToken",

    // Return
    "returnDate",
    "returnRemarks",
    "returnHistory",

    // Disposal
    "disposalDate",
    "disposalReason",
    "disposalRemarks",
    "disposalHistory",
    "quantityHistory",
    "quantityAdjustments",

    // Approval 
    "approvalStatus",
    "approvalHistory",
    "approverList",

    // Metadata
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "deletedFlag",
    "modifiedAt",
    "modifiedBy",
  ]);

  static FIELD_DEFAULTS = Object.freeze({
    // Arrays
    assignmentHistory: [],
    locationHistory: [],
    warrantyHistory: [],
    returnHistory: [],
    disposalHistory: [],
    quantityHistory: [],
    quantityAdjustments: [],
    depreciationSchedule: [],
    ownershipHistory: [],
    approvalHistory: [],
    approverList: [],

    // Booleans
    purchasedSeparately: false,
    isOpeningBookValueManual: false,
    isDepreciationAuto: true,
    isTagged: false, 

    // Numeric
    quantity: 1,

    // Strings
    status: "Available",
    serializationType: "serialized",
    approvalStatus: "Draft",

    // Ownership fields
    ownerId: null,
    ownerEmail: null,
    ownerName: null,
    ownerAssignedOn: null,
  });

  constructor(data = {}, { isCreation = false } = {}) {
    // 1. Initialize every allowed key with its default
    for (const key of AssetModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          deletedFlag: false,
          modifiedAt: null,
          modifiedBy: null,
        };
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(AssetModel.FIELD_DEFAULTS, key)) {
        const def = AssetModel.FIELD_DEFAULTS[key];
        this[key] = Array.isArray(def) ? [] : def;
      } else {
        this[key] = null;
      }
    }

    // 2. Apply incoming data (only allowed keys)
    for (const key of Object.keys(data)) {
      if (!AssetModel.ALLOWED_KEYS.includes(key)) continue;

      if (key === "metadata") {
        const incomingMeta = data.metadata || {};
        const cleanedMeta = {};
        for (const metaKey of AssetModel.ALLOWED_METADATA_KEYS) {
          if (metaKey === "deletedFlag") {
            cleanedMeta[metaKey] = incomingMeta[metaKey] ?? false;
          } else {
            cleanedMeta[metaKey] = incomingMeta[metaKey] ?? null;
          }
        }
        this.metadata = cleanedMeta;
        continue;
      }

      const value = data[key];
      const currentDefault = AssetModel.FIELD_DEFAULTS[key];

      if (Array.isArray(currentDefault)) {
        this[key] = Array.isArray(value) ? value : [];
      } else if (
        currentDefault !== null &&
        typeof currentDefault === "object" &&
        !Array.isArray(currentDefault)
      ) {
        this[key] =
          value !== null && typeof value === "object" && !Array.isArray(value)
            ? value
            : { ...currentDefault };
      } else if (typeof currentDefault === "boolean") {
        this[key] = typeof value === "boolean" ? value : currentDefault;
      } else if (key === "quantity") {
        const parsed = Number(value);
        this[key] = !isNaN(parsed) && parsed >= 0 ? parsed : 1;
      } else {
        this[key] = value;
      }
    }

    // Check serialization type: force quantity = 1 if item is serialized
    const normSerialization = String(this.serializationType || "").toLowerCase().trim();
    const isNonSerialized = normSerialization === "non-serialized" || normSerialization === "bulk";

    if (!isNonSerialized) {
      this.quantity = 1;
    }

    // 3. Creation-only defaults
    if (isCreation) {
      const now = new Date().toISOString();

      const generateUUID = () =>
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

      this.id = data.id || data.assetId || generateUUID();
      this.assetId = data.assetId || this.id;
      this.assetName = data.assetName || "";
      this.companyId = data.companyId || "";
      this.assetType = data.assetType || "";
      this.ownershipType = data.ownershipType || "";
      this.status = data.status || "Available";

      // Accept input aliases: qty, count → quantity for non-serialized items
      if (isNonSerialized) {
        const rawQty = data.quantity ?? data.qty ?? data.count;
        const parsedQty = Number(rawQty);
        this.quantity = !isNaN(parsedQty) && parsedQty >= 0 ? parsedQty : 1;
      } else {
        this.quantity = 1;
      }

      // Accept input alias: uom → unitOfMeasurement
      this.unitOfMeasurement = data.unitOfMeasurement || data.uom || null;

      this.serializationType = data.serializationType || "serialized";
      this.purchasedSeparately = data.purchasedSeparately ?? false;
      this.isTagged      = data.isTagged ?? false;   
      this.taggedAt      = data.taggedAt ?? null;   
      this.taggedBy      = data.taggedBy ?? null;    
      this.tagPhotoUrl   = data.tagPhotoUrl ?? null; 

      this.metadata = {
        createdAt: data.metadata?.createdAt || now,
        createdBy: data.metadata?.createdBy || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
        modifiedAt: now,
        modifiedBy: data.metadata?.modifiedBy || "system",
      };
    }
  }

  getValidationErrors() {
    const errors = [];
    if (!this.assetId) errors.push("Asset ID is required.");
    if (!this.assetName) errors.push("Asset Name is required.");
    if (!this.companyId) errors.push("Company ID is required.");
    if (!this.assetType) errors.push("Asset Type is required.");
    if (!this.ownershipType) errors.push("Ownership Type is required.");

    const CHILD_TYPES = ["Sub Asset", "Component", "Accessory"];
    if (CHILD_TYPES.includes(this.assetType) && !this.parentAssetId) {
      errors.push(`Parent Asset is required for ${this.assetType}.`);
    }
    return errors;
  }

  isValid() {
    return this.getValidationErrors().length === 0;
  }

  toPayload({ stripNulls = false } = {}) {
    const payload = {};
    for (const key of AssetModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        const metaPayload = {};
        for (const metaKey of AssetModel.ALLOWED_METADATA_KEYS) {
          const metaValue = this.metadata?.[metaKey] ?? null;
          if (stripNulls && metaValue === null) continue;
          metaPayload[metaKey] = metaValue;
        }
        payload.metadata = metaPayload;
      } else if (this[key] !== undefined) {
        if (stripNulls && this[key] === null) continue;
        payload[key] = this[key];
      }
    }
    return payload;
  }
}